import "webrtc-adapter";
import { SignalingClient } from "amazon-kinesis-video-streams-webrtc";
import {
  CLINICAL_DEVICE_IDS,
  CLINICAL_STREAM_STATES,
  createClinicalViewerClientId,
  createDeviceMessageId,
} from "../deviceWebSocket/deviceMessageProtocol";
import { getKvsWebRtcBootstrap } from "./kvsBootstrap";

const CONNECTION_TIMEOUT_MS = 25_000;
const DISCONNECTED_GRACE_MS = 5_000;
const MEDIA_HEALTH_INTERVAL_MS = 5_000;
const MEDIA_STALL_TIMEOUT_MS = 20_000;
const RETRY_BASE_MS = 1_000;
const RETRY_MAX_MS = 15_000;

const noop = () => {};
const fallbackLogger = { debug: noop, warn: noop };

const getMediaKind = (clinicalDeviceId) => (
  clinicalDeviceId === CLINICAL_DEVICE_IDS.STETHOSCOPE ? "audio" : "video"
);

const retryDelay = (attempt, random) => {
  const ceiling = Math.min(RETRY_BASE_MS * (2 ** Math.min(attempt, 8)), RETRY_MAX_MS);
  return Math.max(250, Math.floor(random() * ceiling));
};

const stopRemoteStream = (stream) => {
  stream?.getTracks?.().forEach((track) => {
    try { track.stop(); } catch {}
  });
};

export class KvsViewerSession {
  constructor({
    onStateChange,
    onRemoteStream,
    logger = typeof console !== "undefined" ? console : fallbackLogger,
    random = Math.random,
    setTimer = setTimeout,
    clearTimer = clearTimeout,
  } = {}) {
    this.onStateChange = onStateChange;
    this.onRemoteStream = onRemoteStream;
    this.logger = logger;
    this.random = random;
    // Browser timer functions can reject a session instance as their receiver
    // ("Illegal invocation") when called through `this.setTimer(...)`.
    this.setTimer = (...args) => setTimer(...args);
    this.clearTimer = (...args) => clearTimer(...args);

    this.desired = false;
    this.disposed = false;
    this.retryAttempt = 0;
    this.retryTimer = null;
    this.connectionTimer = null;
    this.disconnectedTimer = null;
    this.mediaHealthTimer = null;
    this.attemptToken = null;
    this.signalingClient = null;
    this.peerConnection = null;
    this.remoteStream = null;
    this.turnCredentialsExpireAt = null;
    this.remoteCandidateQueue = [];
    this.localCandidateQueue = [];
    this.signalingOpen = false;
    this.makingOffer = false;
    this.pendingOfferCorrelationId = null;
    this.awaitingAnswer = false;
    this.applyingRemoteDescription = false;
    this.iceRestartAttempted = false;
    this.lastMediaBytesReceived = null;
    this.lastMediaProgressAt = 0;
    this.state = CLINICAL_STREAM_STATES.STOPPED;
  }

  start({ kioskDeviceId, organizationId, streamSessionId, clinicalDeviceId }) {
    if (
      !kioskDeviceId
      || !organizationId
      || !streamSessionId
      || !Object.values(CLINICAL_DEVICE_IDS).includes(clinicalDeviceId)
    ) {
      this._setState(CLINICAL_STREAM_STATES.FAILED, {
        errorCode: "invalid-viewer-session",
        message: "Clinical WebRTC session information is incomplete.",
      });
      return false;
    }

    const alreadyActive = this.desired
      && this.streamSessionId === streamSessionId
      && this.clinicalDeviceId === clinicalDeviceId;
    if (alreadyActive) return true;

    this.stop({ emitState: false });
    this.disposed = false;
    this.desired = true;
    this.kioskDeviceId = kioskDeviceId;
    this.organizationId = organizationId;
    this.streamSessionId = streamSessionId;
    this.clinicalDeviceId = clinicalDeviceId;
    this.retryAttempt = 0;
    this._setState(CLINICAL_STREAM_STATES.CONNECTING);
    void this._connect();
    return true;
  }

  stop({ emitState = true } = {}) {
    this.desired = false;
    this.retryAttempt = 0;
    this._clearRetryTimer();
    this._disposeAttempt();
    if (emitState) this._setState(CLINICAL_STREAM_STATES.STOPPED);
  }

  dispose() {
    this.disposed = true;
    this.stop({ emitState: false });
  }

  _setState(state, details = {}) {
    this.state = state;
    this.onStateChange?.({
      state,
      streamSessionId: this.streamSessionId || null,
      deviceId: this.clinicalDeviceId || null,
      ...details,
    });
  }

  _isCurrent(token) {
    return !this.disposed && this.desired && this.attemptToken === token;
  }

  async _connect() {
    if (this.disposed || !this.desired) return;
    if (typeof RTCPeerConnection === "undefined") {
      this.desired = false;
      this._setState(CLINICAL_STREAM_STATES.FAILED, {
        errorCode: "webrtc-unsupported",
        message: "This browser does not support WebRTC.",
      });
      return;
    }

    this._disposeAttempt();
    const token = {};
    this.attemptToken = token;
    this._setState(
      this.retryAttempt > 0
        ? CLINICAL_STREAM_STATES.RECONNECTING
        : CLINICAL_STREAM_STATES.CONNECTING,
      { attempt: this.retryAttempt + 1 },
    );

    let failureReason = "bootstrap-failed";
    try {
      const bootstrap = await getKvsWebRtcBootstrap({
        deviceId: this.kioskDeviceId,
        organizationId: this.organizationId,
        role: "VIEWER",
      });
      if (!this._isCurrent(token)) return;
      failureReason = "signaling-initialization-failed";

      const peerConnection = new RTCPeerConnection({
        iceServers: bootstrap.iceServers,
        iceTransportPolicy: "all",
        bundlePolicy: "max-bundle",
        rtcpMuxPolicy: "require",
      });
      const signalingClient = new SignalingClient({
        channelARN: bootstrap.channelARN,
        channelEndpoint: bootstrap.channelEndpoint,
        role: "VIEWER",
        region: bootstrap.region,
        credentials: bootstrap.credentials,
        clientId: createClinicalViewerClientId(this.streamSessionId),
      });

      this.peerConnection = peerConnection;
      this.signalingClient = signalingClient;
      this.turnCredentialsExpireAt = bootstrap.turnCredentialsExpireAt;
      this.remoteCandidateQueue = [];
      this.localCandidateQueue = [];
      this.signalingOpen = false;
      this.iceRestartAttempted = false;
      this.lastMediaBytesReceived = null;
      this.lastMediaProgressAt = Date.now();
      this.awaitingAnswer = false;
      this.applyingRemoteDescription = false;

      peerConnection.addTransceiver(getMediaKind(this.clinicalDeviceId), {
        direction: "recvonly",
      });

      peerConnection.onicecandidate = ({ candidate }) => {
        if (!candidate || !this._isCurrent(token)) return;
        if (this.signalingOpen) {
          try { signalingClient.sendIceCandidate(candidate); } catch (error) {
            this.logger?.warn?.("[clinical-webrtc-viewer] Unable to send ICE candidate", {
              error: error?.message,
            });
          }
        } else {
          this.localCandidateQueue.push(candidate);
        }
      };
      peerConnection.onicecandidateerror = (event) => {
        this.logger?.warn?.("[clinical-webrtc-viewer] ICE candidate error", {
          errorCode: event?.errorCode,
          errorText: event?.errorText,
        });
      };
      peerConnection.ontrack = (event) => this._handleRemoteTrack(event, token);
      peerConnection.onconnectionstatechange = () => this._handleConnectionState(token);
      peerConnection.oniceconnectionstatechange = () => this._handleIceState(token);

      signalingClient.on("open", () => {
        if (!this._isCurrent(token)) return;
        this.signalingOpen = true;
        const queuedCandidates = this.localCandidateQueue.splice(0);
        queuedCandidates.forEach((candidate) => {
          try { signalingClient.sendIceCandidate(candidate); } catch {}
        });
        void this._sendOffer(token).catch((error) => this._scheduleReconnect(
          "offer-failed",
          error,
          token,
        ));
      });
      signalingClient.on("sdpAnswer", (answer) => {
        if (!this._isCurrent(token)) return;
        this.applyingRemoteDescription = true;
        void this._handleSdpAnswer(answer, token);
      });
      signalingClient.on("iceCandidate", (candidate) => {
        void this._handleRemoteCandidate(candidate, token);
      });
      signalingClient.on("statusResponse", (response) => {
        if (
          this._isCurrent(token)
          && response?.correlationId === this.pendingOfferCorrelationId
        ) {
          const error = new Error(response?.description || "KVS rejected the SDP offer");
          this._scheduleReconnect("signaling-offer-rejected", error, token);
        }
      });
      signalingClient.on("close", () => {
        if (this._isCurrent(token)) this._scheduleReconnect("signaling-closed");
      });
      signalingClient.on("error", (error) => {
        if (this._isCurrent(token)) this._scheduleReconnect("signaling-error", error);
      });

      signalingClient.open();
      this._armConnectionTimeout(token, "connection-timeout");
    } catch (error) {
      if (this._isCurrent(token)) this._scheduleReconnect(failureReason, error);
    }
  }

  async _sendOffer(token, { iceRestart = false } = {}) {
    if (!this._isCurrent(token) || this.makingOffer || !this.signalingOpen) return;
    const peerConnection = this.peerConnection;
    if (!peerConnection || peerConnection.signalingState !== "stable") return;

    this.makingOffer = true;
    try {
      const offer = await peerConnection.createOffer({ iceRestart });
      if (!this._isCurrent(token)) return;
      await peerConnection.setLocalDescription(offer);
      if (!this._isCurrent(token)) return;
      this.awaitingAnswer = true;
      const correlationId = createDeviceMessageId(
        iceRestart ? "clinical-ice-restart" : "clinical-offer",
      );
      this.pendingOfferCorrelationId = correlationId;
      try {
        this.signalingClient.sendSdpOffer(
          peerConnection.localDescription,
          undefined,
          correlationId,
        );
      } catch (error) {
        this.awaitingAnswer = false;
        throw error;
      }
      this._armConnectionTimeout(token, iceRestart ? "ice-restart-timeout" : "negotiation-timeout");
    } finally {
      this.makingOffer = false;
    }
  }

  async _handleSdpAnswer(answer, token) {
    if (!this._isCurrent(token) || !this.peerConnection) return;
    try {
      await this.peerConnection.setRemoteDescription(answer);
      const queuedCandidates = this.remoteCandidateQueue.splice(0);
      for (const candidate of queuedCandidates) {
        if (!this._isCurrent(token)) return;
        try {
          await this.peerConnection.addIceCandidate(candidate);
        } catch (error) {
          this.logger?.warn?.("[clinical-webrtc-viewer] Ignoring queued ICE candidate", {
            error: error?.message,
          });
        }
      }
      this.awaitingAnswer = false;
      this.pendingOfferCorrelationId = null;
      this.applyingRemoteDescription = false;
    } catch (error) {
      this.awaitingAnswer = false;
      this.applyingRemoteDescription = false;
      if (this._isCurrent(token)) this._scheduleReconnect("invalid-sdp-answer", error);
    }
  }

  async _handleRemoteCandidate(candidate, token) {
    if (!this._isCurrent(token) || !this.peerConnection) return;
    if (
      this.awaitingAnswer
      || this.applyingRemoteDescription
      || !this.peerConnection.remoteDescription
    ) {
      this.remoteCandidateQueue.push(candidate);
      return;
    }
    try {
      await this.peerConnection.addIceCandidate(candidate);
    } catch (error) {
      this.logger?.warn?.("[clinical-webrtc-viewer] Ignoring unusable remote ICE candidate", {
        error: error?.message,
      });
    }
  }

  _handleRemoteTrack(event, token) {
    if (!this._isCurrent(token)) return;
    const incomingStream = event.streams?.[0];
    if (incomingStream) {
      this.remoteStream = incomingStream;
    } else {
      if (!this.remoteStream) this.remoteStream = new MediaStream();
      this.remoteStream.addTrack(event.track);
    }
    this.onRemoteStream?.(this.remoteStream);
    event.track.onended = () => {
      if (this._isCurrent(token)) this._scheduleReconnect("remote-track-ended");
    };
  }

  _handleConnectionState(token) {
    if (!this._isCurrent(token) || !this.peerConnection) return;
    const { connectionState } = this.peerConnection;
    if (connectionState === "connected") {
      this.retryAttempt = 0;
      this.iceRestartAttempted = false;
      this.lastMediaBytesReceived = null;
      this.lastMediaProgressAt = Date.now();
      this._clearConnectionTimer();
      this._clearDisconnectedTimer();
      this._setState(CLINICAL_STREAM_STATES.STREAMING);
      this._startMediaHealthMonitor(token);
    } else if (connectionState === "disconnected") {
      this._beginDisconnectedGrace(token);
    } else if (connectionState === "failed") {
      void this._attemptIceRestart(token);
    }
  }

  _handleIceState(token) {
    if (!this._isCurrent(token) || !this.peerConnection) return;
    const { iceConnectionState } = this.peerConnection;
    if (iceConnectionState === "connected" || iceConnectionState === "completed") {
      this._clearDisconnectedTimer();
    } else if (iceConnectionState === "disconnected") {
      this._beginDisconnectedGrace(token);
    } else if (iceConnectionState === "failed") {
      void this._attemptIceRestart(token);
    }
  }

  _beginDisconnectedGrace(token) {
    if (this.disconnectedTimer || !this._isCurrent(token)) return;
    this._setState(CLINICAL_STREAM_STATES.RECONNECTING, {
      errorCode: "peer-disconnected",
    });
    this.disconnectedTimer = this.setTimer(() => {
      this.disconnectedTimer = null;
      void this._attemptIceRestart(token);
    }, DISCONNECTED_GRACE_MS);
  }

  async _attemptIceRestart(token) {
    if (!this._isCurrent(token)) return;
    this._clearDisconnectedTimer();
    if (
      !this.turnCredentialsExpireAt
      || Date.now() >= this.turnCredentialsExpireAt
    ) {
      this._scheduleReconnect("turn-credentials-expired", undefined, token);
      return;
    }
    if (
      this.iceRestartAttempted
      || !this.signalingOpen
      || this.peerConnection?.signalingState !== "stable"
    ) {
      this._scheduleReconnect("peer-connection-failed");
      return;
    }

    this.iceRestartAttempted = true;
    this._setState(CLINICAL_STREAM_STATES.RECONNECTING, {
      errorCode: "ice-restart",
    });
    try {
      await this._sendOffer(token, { iceRestart: true });
    } catch (error) {
      if (this._isCurrent(token)) this._scheduleReconnect("ice-restart-failed", error);
    }
  }

  _scheduleReconnect(reason, error, token = null) {
    if (
      this.disposed
      || !this.desired
      || this.retryTimer
      || (token && !this._isCurrent(token))
    ) return;
    this.logger?.warn?.("[clinical-webrtc-viewer] Rebuilding WebRTC session", {
      reason,
      error: error?.message,
      attempt: this.retryAttempt + 1,
    });
    this._disposeAttempt();
    const delay = retryDelay(this.retryAttempt, this.random);
    this.retryAttempt += 1;
    this._setState(CLINICAL_STREAM_STATES.RECONNECTING, {
      errorCode: reason,
      retryDelayMs: delay,
      ...(error?.message ? { message: error.message } : {}),
    });
    this.retryTimer = this.setTimer(() => {
      this.retryTimer = null;
      void this._connect();
    }, delay);
  }

  _startMediaHealthMonitor(token) {
    this._clearMediaHealthTimer();
    if (typeof this.peerConnection?.getStats !== "function") return;

    const expectedKind = getMediaKind(this.clinicalDeviceId);

    const inspectMedia = async () => {
      if (!this._isCurrent(token) || this.state !== CLINICAL_STREAM_STATES.STREAMING) return;
      try {
        const reports = await this.peerConnection.getStats();
        if (!this._isCurrent(token)) return;
        let bytesReceived = 0;
        let foundInboundMedia = false;
        reports.forEach((report) => {
          const mediaKind = report.kind || report.mediaType;
          if (report.type === "inbound-rtp" && !report.isRemote && mediaKind === expectedKind) {
            foundInboundMedia = true;
            bytesReceived += Number(report.bytesReceived || 0);
          }
        });

        if (
          foundInboundMedia
          && (
            this.lastMediaBytesReceived === null
            || bytesReceived !== this.lastMediaBytesReceived
          )
        ) {
          this.lastMediaBytesReceived = bytesReceived;
          this.lastMediaProgressAt = Date.now();
        } else if (Date.now() - this.lastMediaProgressAt >= MEDIA_STALL_TIMEOUT_MS) {
          this._scheduleReconnect(`${expectedKind}-media-stalled`, undefined, token);
          return;
        }
      } catch (error) {
        this.logger?.debug?.("[clinical-webrtc-viewer] Unable to read peer statistics", {
          error: error?.message,
        });
      }

      if (this._isCurrent(token)) {
        this.mediaHealthTimer = this.setTimer(inspectMedia, MEDIA_HEALTH_INTERVAL_MS);
      }
    };

    this.mediaHealthTimer = this.setTimer(inspectMedia, MEDIA_HEALTH_INTERVAL_MS);
  }

  _armConnectionTimeout(token, reason) {
    this._clearConnectionTimer();
    this.connectionTimer = this.setTimer(() => {
      this.connectionTimer = null;
      if (this._isCurrent(token) && this.state !== CLINICAL_STREAM_STATES.STREAMING) {
        this._scheduleReconnect(reason);
      }
    }, CONNECTION_TIMEOUT_MS);
  }

  _clearRetryTimer() {
    if (this.retryTimer) this.clearTimer(this.retryTimer);
    this.retryTimer = null;
  }

  _clearConnectionTimer() {
    if (this.connectionTimer) this.clearTimer(this.connectionTimer);
    this.connectionTimer = null;
  }

  _clearDisconnectedTimer() {
    if (this.disconnectedTimer) this.clearTimer(this.disconnectedTimer);
    this.disconnectedTimer = null;
  }

  _clearMediaHealthTimer() {
    if (this.mediaHealthTimer) this.clearTimer(this.mediaHealthTimer);
    this.mediaHealthTimer = null;
  }

  _disposeAttempt() {
    this.attemptToken = null;
    this._clearConnectionTimer();
    this._clearDisconnectedTimer();
    this._clearMediaHealthTimer();
    this.signalingOpen = false;
    this.turnCredentialsExpireAt = null;
    this.makingOffer = false;
    this.pendingOfferCorrelationId = null;
    this.awaitingAnswer = false;
    this.applyingRemoteDescription = false;
    this.remoteCandidateQueue = [];
    this.localCandidateQueue = [];

    const signalingClient = this.signalingClient;
    const peerConnection = this.peerConnection;
    const remoteStream = this.remoteStream;
    this.signalingClient = null;
    this.peerConnection = null;
    this.remoteStream = null;

    try { signalingClient?.close(); } catch {}
    try { signalingClient?.removeAllListeners?.(); } catch {}
    if (peerConnection) {
      peerConnection.onicecandidate = null;
      peerConnection.ontrack = null;
      peerConnection.onconnectionstatechange = null;
      peerConnection.oniceconnectionstatechange = null;
      try { peerConnection.close(); } catch {}
    }
    stopRemoteStream(remoteStream);
    this.onRemoteStream?.(null);
  }
}
